import { FilterIcon, Select } from '@bloomfieldrobotics/canopy';
import { IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { Scan } from '../../../../services/entities/impl/blfApi/schemas/scan';
import { Stage } from '../../../../services/entities/impl/blfApi/schemas/stage';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import {
  MapControl,
  MapControlContent,
  MapControlHeader,
  MapControlSection,
} from '../../../commonUI/maps/controls';
import { StageSelect } from '../../../reports/blueberry/components/StageSelect';
import { useGetBlockStageCounts } from '../hooks/useGetBlockStageCounts';
import { BlockStageDataDisplay } from './StageDataDisplay/BlockStageDataDisplay/BlockStageDataDisplay';
import {
  BlockPointsMapControlActionTypes,
  BlockPointsMapControlFilters,
} from './types';

type Props = {
  block: Block;
  scanId: Scan['id'];
  stages: Stage[];
  loading: boolean;
  filters: BlockPointsMapControlFilters;
  onControlAction: (
    actionType: BlockPointsMapControlActionTypes,
    payload: BlockPointsMapControlFilters
  ) => void;
};

export const BlockDetailsControl = ({
  block,
  scanId,
  stages,
  loading,
  onControlAction,
  filters,
}: Props) => {
  const { t } = useTranslation();

  /** Filtering Logic & State */

  const displayStages =
    filters.stages && filters.stages.length > 0
      ? stages.filter((stage) => filters.stages?.includes(stage.id))
      : stages;
  const [showFilterBar, setShowFilterBar] = useState<boolean>(
    !!filters.stages || !!filters.sizes
  );
  const handleStageFilterChange = useCallback(
    (newStagesFilter: BlockPointsMapControlFilters['stages']) => {
      onControlAction('FILTER', { ...filters, stages: newStagesFilter });
    },
    [onControlAction, filters]
  );

  /** Data Fetching */
  const { stageCounts } = useGetBlockStageCounts(
    block.id,
    scanId,
    filters.stages && filters.stages?.length > 0
      ? filters.stages
      : displayStages.map((s) => s.id),
    loading
  );

  const headerActions = (
    <IconButton
      sx={{
        color: (theme) =>
          showFilterBar
            ? theme.vars.palette.action.active
            : theme.vars.palette.text.secondary,
      }}
      size='small'
      onClick={() => setShowFilterBar((prevState) => !prevState)}
    >
      <FilterIcon fontSize='inherit' />
    </IconButton>
  );

  return (
    <MapControl
      aria-label='Block Metadata'
      tabIndex={-1}
      sx={{
        position: 'absolute',
        left: 8,
        top: 109,
        width: 350,
      }}
    >
      <MapControlHeader
        title={t('Berry count')}
        actions={headerActions}
        sx={{ mb: 2 }}
      />

      <MapControlContent>
        {showFilterBar && (
          <MapControlSection sx={{ mb: 2 }}>
            <Stack
              direction='row'
              spacing={2}
              sx={{ width: '100%', paddingTop: 4, paddingBottom: 4 }}
            >
              <Stack direction='row' spacing={2} sx={{ minWidth: 100 }}>
                <Typography variant='body2' noWrap>
                  View By
                </Typography>
                <Select
                  variant='minimal'
                  value={'stage'}
                  onChange={() => {}}
                  options={[
                    { value: 'stage', label: 'Stage' },
                    // { value: 'size', label: 'Size' },
                  ]}
                />
              </Stack>
              <Stack
                direction='row'
                spacing={2}
                sx={{ minWidth: 100, maxWidth: 150, margin: 'auto' }}
              >
                <Typography variant='body2'>Stage</Typography>
                <StageSelect
                  value={displayStages.map((s) => s.id)}
                  onChange={handleStageFilterChange}
                  varietyId={block.varietyID || ''}
                />
              </Stack>
            </Stack>
          </MapControlSection>
        )}

        <MapControlSection sx={{ paddingBottom: 5 }}>
          <BlockStageDataDisplay
            stageCounts={stageCounts}
            stages={displayStages}
          />
        </MapControlSection>
      </MapControlContent>
    </MapControl>
  );
};
